import './index.css';
import { useEffect, useState, Suspense, lazy, Component } from 'react';
import Navbar from './components/Navbar';
import LeavesCanvas from './components/LeavesCanvas';
import CursorTrail from './components/CursorTrail';
import ScrollProgress from './components/ScrollProgress';
import BackToTop from './components/BackToTop';
import Footer from './components/Footer';
import Chatbot from './components/Chatbot';
import useSEO from './useSEO';
import { loadPortfolio, preloadPortfolioThumbs } from './data/portfolioStore';
import { loadVideos, preloadVideoThumbs } from './data/videosStore';

// HomePage loads eagerly (it's the most common first paint).
// Every other route is code-split so visiting "/" doesn't pull in
// the JS for pages the visitor may never open — this cuts the
// initial bundle size and Total Blocking Time significantly.
import HomePage from './pages/HomePage';

const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const TechnologiesPage = lazy(() => import('./pages/TechnologiesPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const PortfolioPage = lazy(() => import('./pages/PortfolioPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const VideosPage = lazy(() => import('./pages/VideosPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const TestimonialsPage = lazy(() => import('./pages/TestimonialsPage'));
const BlogDetailPage = lazy(() => import('./pages/BlogDetailPage'));
const Admin = lazy(() => import('./sections/Admin'));
const SoftwaresPage = lazy(() => import('./pages/SoftwaresPage'));
const SoftwareDetailPage = lazy(() => import('./pages/SoftwaresPage').then(m => ({ default: m.SoftwareDetailPage })));

const WebsiteDevelopmentPage = lazy(() => import('./pages/services/WebsiteDevelopmentPage'));
const ShopifyEcommercePage = lazy(() => import('./pages/services/ShopifyEcommercePage'));
const DigitalMarketingPage = lazy(() => import('./pages/services/DigitalMarketingPage'));
const SeoPage = lazy(() => import('./pages/services/SeoPage'));
const GoogleAdsPage = lazy(() => import('./pages/services/GoogleAdsPage'));
const MetaAdsPage = lazy(() => import('./pages/services/MetaAdsPage'));
const SocialMediaManagementPage = lazy(() => import('./pages/services/SocialMediaManagementPage'));
const GraphicDesignPage = lazy(() => import('./pages/services/GraphicDesignPage'));
const AiSolutionsPage = lazy(() => import('./pages/services/AiSolutionsPage'));
const CustomSoftwareDevelopmentPage = lazy(() => import('./pages/services/CustomSoftwareDevelopmentPage'));

// If a page throws while rendering, show a small message instead of letting
// React unmount the whole site (which leaves a blank white screen).
class PageErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err) { console.error('Page crashed:', err); }
  render() {
    if (this.state.failed) {
      return (
        <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem', padding: '8rem 1.5rem 4rem', textAlign: 'center' }}>
          <h2 className="section-title">Something went wrong</h2>
          <p className="section-sub">Please refresh the page. If it keeps happening, contact us.</p>
          <a href="#/" style={{ color: 'var(--green)', fontWeight: 700 }}>← Back to Home</a>
        </div>
      );
    }
    return this.props.children;
  }
}

// Simple hash-based routing (no react-router needed)
function useRoute() {
  const [hash, setHash] = useState(window.location.hash);
  useEffect(() => {
    const onChange = () => setHash(window.location.hash);
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return hash;
}

const routes = {
  '': HomePage,
  '#/': HomePage,
  '#/home': HomePage,
  '#/about': AboutPage,
  '#/services': ServicesPage,
  '#/technologies': TechnologiesPage,
  '#/services/website-development': WebsiteDevelopmentPage,
  '#/services/shopify-ecommerce': ShopifyEcommercePage,
  '#/services/digital-marketing': DigitalMarketingPage,
  '#/services/seo': SeoPage,
  '#/services/google-ads': GoogleAdsPage,
  '#/services/meta-ads': MetaAdsPage,
  '#/services/social-media-management': SocialMediaManagementPage,
  '#/services/graphic-design': GraphicDesignPage,
  '#/services/ai-solutions': AiSolutionsPage,
  '#/services/custom-software-development': CustomSoftwareDevelopmentPage,
  '#/portfolio': PortfolioPage,
  '#/softwares': SoftwaresPage,
  '#/videos': VideosPage,
  '#/blog': BlogPage,
  '#/testimonials': TestimonialsPage,
  '#/contact': ContactPage,
};

function App() {
  const route = useRoute();

  // Keep <title>, meta description, canonical & OG tags in sync with the page
  useSEO(route);

  // Background prefetch: once the first page has finished loading, quietly
  // download the Portfolio and Videos pages' code, data and thumbnails,
  // so clicking "Portfolio" or "Videos" later shows everything instantly.
  useEffect(() => {
    let idleId, timer;
    const warm = () => {
      import('./pages/PortfolioPage').catch(() => {});
      loadPortfolio().then(preloadPortfolioThumbs).catch(() => {});
      import('./pages/VideosPage').catch(() => {});
      loadVideos().then(items => preloadVideoThumbs(items)).catch(() => {});
    };
    const schedule = () => {
      if ('requestIdleCallback' in window) idleId = window.requestIdleCallback(warm, { timeout: 3000 });
      else timer = setTimeout(warm, 1500);
    };
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });
    return () => {
      window.removeEventListener('load', schedule);
      if (idleId && 'cancelIdleCallback' in window) window.cancelIdleCallback(idleId);
      clearTimeout(timer);
    };
  }, []);

  // Scroll to top whenever the page changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [route]);

  // Scroll reveal observer — re-attach on every page change
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) e.target.classList.add('visible');
      }),
      { threshold: 0.12 }
    );

    const scan = () => {
      document.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale')
        .forEach(el => observer.observe(el)); // re-observing same el is a no-op
    };

    // Initial scan once the new page's elements are in the DOM
    const t = setTimeout(scan, 50);

    // Re-scan whenever new elements appear later (e.g. images fetched
    // from the backend render AFTER the initial scan — without this
    // they stay invisible/blank)
    let debounce;
    const mo = new MutationObserver(() => {
      clearTimeout(debounce);
      debounce = setTimeout(scan, 80);
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearTimeout(t);
      clearTimeout(debounce);
      mo.disconnect();
      observer.disconnect();
    };
  }, [route]);

  // Admin route (no navbar/footer)
  if (route === '#/admin') {
    return (
      <Suspense fallback={null}>
        <Admin />
      </Suspense>
    );
  }

  // Blog detail route: #/blog/<id> — not in the static `routes` map since
  // the id is dynamic, so it's matched separately here.
  const blogDetailMatch = route.match(/^#\/blog\/([^/]+)$/);
  const softwareDetailMatch = route.match(/^#\/softwares\/([^/]+)$/);

  const Page = (blogDetailMatch || softwareDetailMatch) ? null : (routes[route] || HomePage);

  return (
    <>
      <ScrollProgress />
      <CursorTrail />
      <LeavesCanvas />
      <Navbar currentRoute={route} />
      <div className="page-fade-in" key={route}>
        <PageErrorBoundary key={route}>
        <Suspense fallback={<div style={{ minHeight: '60vh' }} />}>
          {blogDetailMatch
            ? <BlogDetailPage postId={blogDetailMatch[1]} />
            : softwareDetailMatch
            ? <SoftwareDetailPage softwareId={softwareDetailMatch[1]} />
            : <Page />}
        </Suspense>
        </PageErrorBoundary>
      </div>
      <Footer />
      <Chatbot />
      <BackToTop />
    </>
  );
}

export default App;
